import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = `/#${sectionId}`;
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: 'Services', id: 'services' },
    { label: 'About', id: 'about' },
    { label: 'Biography', id: 'founder' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <div className={`w-16 h-16 rounded-lg flex items-center justify-center overflow-hidden ${isScrolled ? 'bg-helix-dark' : 'bg-helix-dark'}`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_site-clone-quiz/artifacts/0dzx8831_image.png"
                alt="HelixAspire Logo"
                className="w-12 h-12 object-contain"
              />
            </div>
            <div className="ml-3">
              <span className={`text-xl font-bold ${isScrolled ? 'text-helix-dark' : 'text-white'}`}>
                helixaspire
              </span>
              <span className={`block text-[8px] tracking-[0.2em] uppercase ${isScrolled ? 'text-gray-500' : 'text-white/70'}`}>
                Innovation. Curiosity. Courage.
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => scrollToSection(link.id)}
                className={`text-sm font-medium transition-colors hover:text-helix-orange ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {link.label}
              </button>
            ))}
            <button
              data-testid="nav-get-in-touch"
              onClick={() => scrollToSection('contact')}
              className="bg-helix-orange hover:bg-helix-orange-dark text-white px-6 py-2.5 rounded-lg font-medium text-sm transition-all duration-200"
            >
              Get in Touch
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-4 bg-white rounded-lg shadow-lg mt-2">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-helix-orange"
              >
                {link.label}
              </button>
            ))}
            <div className="px-4 pt-2">
              <button
                onClick={() => scrollToSection('contact')}
                className="w-full bg-helix-orange hover:bg-helix-orange-dark text-white px-6 py-2.5 rounded-lg font-medium text-sm transition-all duration-200"
              >
                Get in Touch
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
